import Store from '@ember-data/store';
import Controller from '@ember/controller';
import { action } from '@ember/object';
import Router from '@ember/routing/router';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { type FundMappingsEditRouteModel } from 'donor-elf-web/routes/admin/fund-mappings/edit';
import FundMappingsService from 'donor-elf-web/services/fund-mappings-service';
import {
  EditFundMappingHostIdentityValidation,
  EditFundMappingValidation,
} from 'donor-elf-web/validations/admin/edit-fund-mapping';
import Changeset from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';

export default class FundMappingsEditController extends Controller {
  declare model: FundMappingsEditRouteModel;

  @service declare fundMappingsService: FundMappingsService;
  @service declare store: Store;
  @service declare router: Router;

  @tracked saving = false;
  @tracked showValidationErrors = false;
  @tracked error: any;
  @tracked changeset: any;
  @tracked hostIdentityChangesets: any[] = [];

  @action
  templateDidInsert() {
    this.saving = false;
    this.error = undefined;
    this.showValidationErrors = false;
    let editFundMapping = this.store.createRecord('admin/edit-fund-mapping');
    let fund = this.model.fundMapping?.fund;
    if (fund) {
      editFundMapping.fundId = fund.id || 'add';
      editFundMapping.fundName = fund.name;
      editFundMapping.fundActive = fund.active;
    }
    this.changeset = new Changeset(
      editFundMapping,
      lookupValidator(EditFundMappingValidation),
      EditFundMappingValidation,
    );
    this.hostIdentityChangesets = [];
    this.model.fundMapping?.hostIdentities?.forEach((x) => {
      this.createHostIdentityChangeset({
        database: x.database,
        hostId: x.hostId,
      });
    });
  }

  @action
  createHostIdentityChangeset({
    database,
    hostId,
  }: {
    database?: string;
    hostId?: string;
  }) {
    let validation = EditFundMappingHostIdentityValidation.initialize({
      editFundMappingChangeset: this.changeset,
      fundMappingsService: this.fundMappingsService,
    });
    // @ts-expect-error
    let changeset = new Changeset(
      { database, hostId },
      lookupValidator(validation),
      validation,
    );

    this.hostIdentityChangesets = [...this.hostIdentityChangesets, changeset];
  }

  @action
  handleDeleteHostIdentity(hostIdentityChangeset: any) {
    this.hostIdentityChangesets = this.hostIdentityChangesets.filter(
      (x) => x !== hostIdentityChangeset,
    );
  }

  @action
  async save() {
    this.saving = true;
    try {
      this.changeset.set(
        'hostIdentities',
        this.hostIdentityChangesets.map((x) => {
          return {
            database: x.get('database'),
            hostId: x.get('hostId'),
          };
        }),
      );
      for (let changeset of this.hostIdentityChangesets) {
        await changeset.validate();
      }
      await this.changeset.validate();
      this.showValidationErrors = true;

      if (
        this.changeset.get('isValid') &&
        this.hostIdentityChangesets.every((x) => x.get('isValid'))
      ) {
        await this.changeset.save();

        let fundId = this.changeset.get('id');
        let fund = await this.store.findRecord('admin/fund', fundId, {
          forceReload: true,
        });
        let fundHostIdentities = (
          await this.store.query('admin/fund-host-identity', {
            filter: { fund_id: fundId },
          })
        ).slice();
        this.fundMappingsService.updateMappingsForFund(
          fund,
          fundHostIdentities,
        );
        this.router.transitionTo('admin.fund-mappings.index');
      }
      this.saving = false;
    } catch (error) {
      this.error = error;
      this.saving = false;
    }
  }
}
